import { useCallback, useEffect, useState } from 'react';
import { fetchJobs, retryJob } from './services/lms-provisioning.service';

import { JOB_TYPE } from './utils/provisioning-jobs.utils';

const LOCATION_BULK = JOB_TYPE.LOCATION_BULK_PROVISION;

/**
 * Loads fleet-wide location bulk provisioning jobs for the Facility LMS tab.
 * Use the panel Refresh button or reopen the panel to reload; there is no
 * automatic polling while jobs are in progress.
 */
export default function useFacilityBulkJobsHook({ enabled = true, limit = 15 } = {}) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async ({ background = false } = {}) => {
      if (!enabled) return;
      if (!background) {
        setLoading(true);
      }
      setError(null);
      try {
        const result = await fetchJobs({ limit, offset: 0 });
        const rows = Array.isArray(result) ? result : result?.jobs ?? [];
        setJobs(rows.filter((job) => job?.jobType === LOCATION_BULK));
      } catch (err) {
        console.error('[useFacilityBulkJobsHook] Failed to load bulk jobs', err);
        setError(err);
      } finally {
        if (!background) {
          setLoading(false);
        }
      }
    },
    [enabled, limit]
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    load();
  }, [enabled, load]);

  const retry = useCallback(
    async (jobId) => {
      setSubmitting(true);
      try {
        await retryJob(jobId);
        await load({ background: true });
      } catch (err) {
        console.error('[useFacilityBulkJobsHook] Retry failed', err);
        setError(err);
      } finally {
        setSubmitting(false);
      }
    },
    [load]
  );

  return { jobs, loading, error, submitting, refresh: load, retry };
}
